import React, { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Stack,
  List,
  ListItem,
  useTheme,
  Paper,
} from "@mui/material";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { useDispatch , useSelector} from "react-redux";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { TOOLS } from "../../../Services/Endpoint";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { Agent } from "../../../Shared-ui/Types/Agent";
import Tool from "../../../Shared-ui/Types/Tool";
import EmptyStateImage from "../../../assets/images/Agents_emptystateimage.svg";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import variables from "../../../assets/styles/variables.module.scss";
import Tooltip from "@mui/material/Tooltip";

interface MyComponentProps {
  Agent: Agent;
  setAgent: React.Dispatch<SetStateAction<Agent>>;
  AgentErr: any;
}

const ToolsTab: React.FC<MyComponentProps> = (props) => {
  const [tools, setTools] = useState<Tool[]>([]);
  const [selectedTools, setselectedTools] = useState<Tool[]>([]);
  const theme = useTheme();
  const dispatch = useDispatch();
  const loaderStatus = useSelector((state: any) => state.LoaderReducer);
  const navigate = useNavigate();

  useEffect(() => {
    getTools();
  }, []);

  const getTools = async () => {
    try {
      dispatch(StartLoading("Tools"));
      const data = await BaseInstance.get(
        `${TOOLS}?skip=${0}&limit=${9999}`
      ).then((res) => res?.data);
      data.items.sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name)
      );
      setTools(data.items);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading("Tools"));
    }
  };

  const handleSelectTool = (tool: Tool) => {
    props.setAgent((preVal) => {
      const toolIds = preVal.tool_ids;
      if (tool.id) {
        const newToolIds = toolIds.includes(tool.id)
          ? toolIds.filter((id) => id !== tool.id)
          : [...toolIds, tool.id];
        return {
          ...preVal,
          tool_ids: newToolIds,
        };
      } else {
        return {
          ...preVal,
          tool_ids: toolIds,
        };
      }
    });
  };

  const clearAll = () => {
    props.setAgent((preVal) => {
      return {
        ...preVal,
        tool_ids: [],
      };
    });
  };

  const [searchTool, setSearchTool] = useState<string>("");
  const isToolsLoading = loaderStatus.isLoading && loaderStatus.pageName === "Tools";
  const noToolsFound = !isToolsLoading && tools.length === 0;

  return (
    <Box sx={{}}>
      <Typography
        variant="subtitle2"
        sx={{ display: { xs: "block", md: "none" }, mt: 1, mb: 2 }}
      >
        Connections
      </Typography>
      <Box sx={{ p: theme.spacing(0), width: "100%", maxWidth: "100%", mt: { xs: 0, md: 2 } }}>
        {isToolsLoading || noToolsFound ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: 6,
              cursor: isToolsLoading ? "default" : "pointer",
            }}
          >
            <Tooltip title={isToolsLoading ? "Tools loading..." : "No tools found"} arrow>
              <img
                src={EmptyStateImage}
                alt={isToolsLoading ? "Tools loading" : "No Tools Found"}
                style={{ width: 120, height: "auto" }}
              />
            </Tooltip>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                textDecoration: "underline",
                cursor: "pointer",
                color: variables.basicColor,
              }}
              {...(noToolsFound && { onClick: () => navigate("/tools") })}
            >
               {isToolsLoading
              ? "Connections loading..."
              : "Click here to Create Connection"}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Available Tools Section */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Available Connections
              </Typography>
              <TextField
                placeholder="Search Connections"
                value={searchTool}
                onChange={(e) => setSearchTool(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="500">
                  Selected Connections ({props.Agent.tool_ids.length})
                </Typography>

                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                  <Button
                    onClick={clearAll}
                    disabled={props.Agent.tool_ids.length === 0}
                    variant="text"
                    // size="small"
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    Clear All
                  </Button>
                </Stack>
              </Box>
              <Stack spacing={1}>
                {tools
                  .filter(
                    (tool) =>
                      tool.name
                        .toLowerCase()
                        .includes(searchTool.toLowerCase()) ||
                      tool.description
                        ?.toLowerCase()
                        .includes(searchTool.toLowerCase())
                  )
                  .map((tool, index) => (
                    <React.Fragment key={tool.id}>
                      {tool.id && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={props.Agent.tool_ids.includes(tool.id)}
                              onChange={() => handleSelectTool(tool)}
                            />
                          }
                          label={
                            <Typography variant="body1" fontWeight="bold">
                              {tool.name}{" "}
                              <Typography
                                component="span"
                                color="text.secondary"
                              >
                                ({tool.description})
                              </Typography>
                            </Typography>
                          }
                        />
                      )}
                    </React.Fragment>
                  ))}
              </Stack>
            </Paper>
          </>
        )}

        {/* {selectedTools.length === 0 ? (
        <Typography color="text.secondary">No tools selected</Typography>
      ) : (
        <List dense>
          {selectedTools.map((tool) => (
            <ListItem key={tool} sx={{ pl: 0 }}>
              <Typography variant="body2">
                • {toolsList.find((t) => t.value === tool)?.label}
              </Typography>
            </ListItem>
          ))}
        </List>
      )} */}
      </Box>
    </Box>
  );
};

export default ToolsTab;
